import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getRCTIDetails, adminUpdateRCTIPricing, sendDraftRCTIToXero, syncRCTIWithXero, voidRCTI, regenerateRCTIPdf, linkRCTIToXeroBill } from '../services/rctiService';
import { getSignedUrl } from '../services/fileService';
import { FaFileInvoiceDollar, FaArrowLeft, FaPaperPlane, FaSync, FaFilePdf, FaBan, FaInfoCircle, FaLink } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';
import ActivityLog from '../components/ActivityLog';

// Helper to format Firestore timestamp to a readable date string
const formatDate = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  return new Date(timestamp._seconds * 1000).toLocaleDateString('en-AU');
};

// Helper to format Firestore timestamp to a readable date and time string
const formatDateTime = (timestamp) => {
    if (!timestamp || !timestamp._seconds) return 'N/A';
    return new Date(timestamp._seconds * 1000).toLocaleString('en-AU');
  };

// A VOIDED/DELETED Xero bill is terminal: it must not be synced back, and it no longer blocks a re-issue
const XERO_TERMINAL_INVOICE_STATUSES = ['VOIDED', 'DELETED'];
// voidRCTI writes 'void', but a Xero sync may have already aligned it to the lowercased Xero status
const VOIDED_LOCAL_STATUSES = ['void', 'voided', 'deleted'];

const parseNumberInput = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

// Money for display, e.g. 60 -> "$60.00"
const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const RCTIDetailsPage = () => {
  const { rctiId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnSearch = location.state?.returnSearch || '';
  // Explicit origin passed by whoever linked here (e.g. the job details screen), so Back is
  // deterministic instead of depending on history depth — it survives a refresh or a direct open.
  const returnTo = location.state?.returnTo || null;
  const backLabel = location.state?.returnLabel || 'Back';

  const [searchParams] = useSearchParams();

  const goBack = () => {
    if (returnTo?.pathname) {
      navigate(returnTo.pathname, { state: returnTo.state || {} });
    } else if (returnSearch) {
      navigate({ pathname: '/invoices', search: returnSearch });
    } else if (searchParams.get('returnTab') === 'rctis') {
      navigate('/invoices?tab=rctis');
    } else {
      navigate(-1);
    }
  };
  const [rcti, setRCTI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [xeroBillInput, setXeroBillInput] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingLineItem, setIsEditingLineItem] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sendToXeroError, setSendToXeroError] = useState(null);
  const [xeroExistsNotice, setXeroExistsNotice] = useState('');
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isRegeneratingPdf, setIsRegeneratingPdf] = useState(false);
  const [lineItemForm, setLineItemForm] = useState({
    Quantity: '',
    UnitAmount: '',
  });

  const fetchRCTIData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRCTIDetails(rctiId);
      
      // Log the received data to help debug field issues
      console.log('🔍 RCTI Data received in RCTIDetailsPage:', {
        rctiId,
        data,
        allKeys: Object.keys(data || {}),
        contactRelatedFields: {
          ContactName: data?.ContactName,
          EmailAddress: data?.EmailAddress,
          XeroContactID: data?.XeroContactID, // Capital ID
          XeroContactId: data?.XeroContactId, // Lowercase d
          ContactEmail: data?.ContactEmail,
          // Check other possible variations
          contactName: data?.contactName,
          emailAddress: data?.emailAddress,
          xeroContactId: data?.xeroContactId,
          contactEmail: data?.contactEmail,
        },
        addressFields: {
          POAddressLine1: data?.POAddressLine1,
          POAddressLine2: data?.POAddressLine2,
          POCity: data?.POCity,
          PORegion: data?.PORegion,
          POPostalCode: data?.POPostalCode,
          POCountry: data?.POCountry,
        }
      });
      
      setRCTI(data);
    } catch (err) {
      setError(err.message || 'Failed to load RCTI details.');
      setRCTI(null);
    } finally {
      setLoading(false);
    }
  }, [rctiId]);

  useEffect(() => {
    setSuccessMessage('');
    setSendToXeroError(null);
    setXeroExistsNotice('');
    fetchRCTIData();
  }, [fetchRCTIData, rctiId]);

  useEffect(() => {
    if (!rcti) {
      return;
    }

    setLineItemForm({
      Quantity: rcti.Quantity ?? '',
      UnitAmount: rcti.UnitAmount ?? '',
    });
  }, [rcti]);

  // An RCTI is sendable until it has been voided or already accepted by Xero; `status` is not a usable gate
  const isVoided = VOIDED_LOCAL_STATUSES.includes(rcti?.status);
  const isXeroInvoiceTerminal = XERO_TERMINAL_INVOICE_STATUSES.includes(rcti?.xero_invoice_status);
  // A terminal Xero bill falls through the backend duplicate guard, so it no longer counts as sent
  const alreadySentToXero = rcti?.xero_submission_status === 'success' && !isXeroInvoiceTerminal;
  const canSendToXero = !!rcti && !isVoided && !alreadySentToXero;
  // Syncing a voided/deleted bill rewrites the local status to 'voided' and breaks every 'void' check
  const canSyncFromXero = !!rcti?.xeroInvoiceId && !isVoided && !isXeroInvoiceTerminal;
  // Manual linking is for an RCTI that failed to reach Xero (or was linked before this existed)
  const canManuallyLink = !!rcti && !isVoided && !rcti.xeroInvoiceId;

  const handleSendToXero = async () => {
    if (!rcti) {
      setSendToXeroError('RCTI not loaded.');
      return;
    }
    if (!canSendToXero) {
      setSendToXeroError(isVoided ? 'This RCTI has been voided and cannot be sent to Xero.' : 'This RCTI has already been sent to Xero.');
      return;
    }
    setIsSending(true);
    setSuccessMessage('');
    setSendToXeroError(null);
    setXeroExistsNotice('');

    let sendAttemptSucceeded = false;
    let alreadyExisted = false;
    let operationMessage = '';

    try {
      const response = await sendDraftRCTIToXero(rcti.id);
      sendAttemptSucceeded = true;
      alreadyExisted = response.alreadyExisted === true;
      operationMessage = response.message || "RCTI successfully sent to Xero and status updated.";
    } catch (err) {
      sendAttemptSucceeded = false;
      operationMessage = err.message || 'Failed to send RCTI to Xero.';
    }

    // Attempt to refresh RCTI data
    try {
      const refreshedData = await getRCTIDetails(rctiId);
      setRCTI(refreshedData);
    } catch (refreshError) {
      console.error("Failed to refresh RCTI data after Xero operation:", refreshError);
    }

    if (sendAttemptSucceeded && alreadyExisted) {
      setXeroExistsNotice(operationMessage);
      setSuccessMessage('');
      setSendToXeroError(null);
    } else if (sendAttemptSucceeded) {
      setSuccessMessage(operationMessage);
      setSendToXeroError(null);
    } else {
      setSendToXeroError(operationMessage);
      setSuccessMessage('');
    }

    setIsSending(false);
  };

  const handleSyncFromXero = async () => {
    if (!rcti?.id || !canSyncFromXero) return;

    setIsSyncing(true);
    setSendToXeroError(null);
    setSuccessMessage('');
    setXeroExistsNotice('');

    try {
      const response = await syncRCTIWithXero(rcti.id);
      console.log('Xero sync response:', response);

      if (response.status === 'success') {
        if (response.data.updatedFields?.length > 0) {
          const updatedRCTI = await getRCTIDetails(rcti.id);
          setRCTI(updatedRCTI);
        }
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message);
      }
    } catch (err) {
      console.error('Error syncing from Xero:', err);
      setSendToXeroError(err.message || 'Failed to sync from Xero');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLinkXeroBill = async () => {
    if (!rcti?.id || !canManuallyLink) return;

    const trimmed = xeroBillInput.trim();
    if (!trimmed) {
      setSendToXeroError('Enter the Xero bill ID or its go.xero.com URL.');
      return;
    }

    setIsLinking(true);
    setSendToXeroError(null);
    setSuccessMessage('');
    setXeroExistsNotice('');

    try {
      const response = await linkRCTIToXeroBill(rcti.id, trimmed);
      if (response.status === 'success') {
        const refreshedData = await getRCTIDetails(rcti.id);
        setRCTI(refreshedData);
        setXeroBillInput('');
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message || 'Failed to link Xero bill.');
      }
    } catch (err) {
      console.error('Error linking Xero bill:', err);
      setSendToXeroError(err.message || 'Failed to link Xero bill.');
    } finally {
      setIsLinking(false);
    }
  };

  const handleVoidRCTI = async () => {
    if (!rcti?.id) return;

    setIsVoiding(true);
    setSendToXeroError(null);
    setSuccessMessage('');
    setXeroExistsNotice('');
    setShowVoidConfirm(false);

    try {
      const response = await voidRCTI(rcti.id);
      console.log('Void RCTI response:', response);

      if (response.status === 'success') {
        // Refresh RCTI data to show updated status
        const updatedRCTI = await getRCTIDetails(rcti.id);
        setRCTI(updatedRCTI);
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message || 'Failed to void RCTI');
      }
    } catch (err) {
      console.error('Error voiding RCTI:', err);
      setSendToXeroError(err.message || 'Failed to void RCTI');
    } finally {
      setIsVoiding(false);
    }
  };

  const handleViewPdf = async () => {
    if (!rcti?.pdfDownloadUrl) return;
    try {
      setIsLoadingPdf(true);
      const signedUrl = await getSignedUrl(rcti.pdfDownloadUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening PDF:', err);
      setError('Failed to open PDF: ' + err.message);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Rebuild the RCTI PDF and refresh its download link (no Xero impact). Useful when a
  // PDF failed to generate at creation time and the record has no pdfDownloadUrl.
  const handleRegeneratePdf = async () => {
    if (!rcti?.id) return;

    setIsRegeneratingPdf(true);
    setSuccessMessage('');
    setSendToXeroError(null);
    setShowRegenerateConfirm(false);

    try {
      const response = await regenerateRCTIPdf(rcti.id);
      const refreshedRCTI = await getRCTIDetails(rcti.id);
      setRCTI(refreshedRCTI);
      setSuccessMessage(response.message || 'RCTI PDF regenerated successfully.');
    } catch (err) {
      console.error('Error regenerating RCTI PDF:', err);
      setSendToXeroError(err.message || 'Failed to regenerate RCTI PDF.');
    } finally {
      setIsRegeneratingPdf(false);
    }
  };

  const handleLineItemInputChange = (event) => {
    const { name, value } = event.target;

    setLineItemForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const handleCancelLineItemEdit = () => {
    if (rcti) {
      setLineItemForm({
        Quantity: rcti.Quantity ?? '',
        UnitAmount: rcti.UnitAmount ?? '',
      });
    }

    setIsEditingLineItem(false);
    setSendToXeroError(null);
  };

  // Validate the edited values, then open the confirmation modal (the save
  // updates the job, regenerates the PDF, void+recreates Xero and resends email).
  const handleRequestSave = () => {
    const quantity = parseNumberInput(lineItemForm.Quantity);
    const unitAmount = parseNumberInput(lineItemForm.UnitAmount);

    if (quantity === null || quantity <= 0) {
      setSendToXeroError('Quantity must be greater than 0.');
      return;
    }

    if (unitAmount === null || unitAmount < 0) {
      setSendToXeroError('Unit Amount must be 0 or greater.');
      return;
    }

    setSendToXeroError(null);
    setShowEditConfirm(true);
  };

  const handleConfirmedSave = async () => {
    if (!rcti?.id) {
      return;
    }

    const quantity = parseNumberInput(lineItemForm.Quantity);
    const unitAmount = parseNumberInput(lineItemForm.UnitAmount);

    setIsUpdating(true);
    setSuccessMessage('');
    setSendToXeroError(null);
    setXeroExistsNotice('');

    try {
      const response = await adminUpdateRCTIPricing(rcti.id, {
        unitPrice: unitAmount,
        quantity,
      });
      const refreshedRCTI = await getRCTIDetails(rcti.id);
      setRCTI(refreshedRCTI);
      setIsEditingLineItem(false);
      const notes = response.warnings?.length ? ` Notes: ${response.warnings.join('; ')}` : '';
      setSuccessMessage((response.message || 'RCTI pricing updated successfully.') + notes);
    } catch (err) {
      console.error('Error updating RCTI pricing:', err);
      setSendToXeroError(err.message || 'Failed to update RCTI pricing.');
    } finally {
      setIsUpdating(false);
      setShowEditConfirm(false);
    }
  };

  if (loading && !isSending && !rcti) {
    return <p className="text-center text-gray-500 py-10">Loading RCTI details...</p>;
  }

  if (error && !rcti) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-red-500 text-lg">Error: {error}</p>
        <button onClick={goBack} className="mt-4 inline-block bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded">
          <FaArrowLeft className="mr-2 inline" /> {returnTo ? backLabel : 'Back to Invoices'}
        </button>
      </div>
    );
  }

  if (!rcti && !loading) {
    return <p className="text-center text-gray-500 py-10">RCTI not found.</p>;
  }
  
  const displayQuantity = isEditingLineItem ? parseNumberInput(lineItemForm.Quantity) : rcti?.Quantity;
  const displayUnitAmount = isEditingLineItem ? parseNumberInput(lineItemForm.UnitAmount) : rcti?.UnitAmount;

  // Compare edited values against the saved ones so we can disable Save when nothing
  // changed, and tell the user in the modal whether the job price will move (the job's
  // FSE price tracks Unit Amount, so it only changes when Unit Amount changes).
  const currentQuantity = rcti?.Quantity ?? null;
  const currentUnitAmount = rcti?.UnitAmount ?? null;
  const editedQuantity = parseNumberInput(lineItemForm.Quantity);
  const editedUnitAmount = parseNumberInput(lineItemForm.UnitAmount);
  const unitAmountChanged = editedUnitAmount !== currentUnitAmount;
  const hasLineItemChanges = editedQuantity !== currentQuantity || unitAmountChanged;
  const hasLinkedJob = !!rcti?.jobId;
  const beforeTotal = currentQuantity != null && currentUnitAmount != null ? currentQuantity * currentUnitAmount : null;
  const afterTotal = editedQuantity != null && editedUnitAmount != null ? editedQuantity * editedUnitAmount : null;

  const amountDue = displayQuantity != null && displayUnitAmount != null
    ? (displayQuantity * displayUnitAmount).toFixed(2)
    : 'N/A';

  const displayStatus = rcti?.xero_submission_status === 'success' && rcti?.xero_invoice_status 
                        ? rcti.xero_invoice_status 
                        : rcti?.status;

  let statusHighlight = 'gray';
  if (rcti?.status === 'void' || rcti?.xero_invoice_status === 'VOIDED') {
    statusHighlight = 'red';
  } else if (rcti?.xero_submission_status === 'success' && rcti?.xero_invoice_status) {
    const xeroStatusLower = rcti.xero_invoice_status.toLowerCase();
    if (xeroStatusLower === 'paid') {
      statusHighlight = 'green';
    } else if (xeroStatusLower === 'authorised' || xeroStatusLower === 'submitted') {
      statusHighlight = 'blue';
    } else if (xeroStatusLower === 'draft') {
      statusHighlight = 'yellow';
    }
  } else if (rcti?.status) {
    const systemStatusLower = rcti.status.toLowerCase();
    if (systemStatusLower === 'paid') {
      statusHighlight = 'green';
    } else if (systemStatusLower === 'draft') {
      statusHighlight = 'yellow';
    }
  }

  // Determine if RCTI can be voided (not already void, and not paid in Xero)
  const canVoid = rcti &&
                  rcti.status !== 'void' &&
                  rcti.xero_invoice_status !== 'VOIDED' &&
                  rcti.xero_invoice_status !== 'PAID';
  // Admins may correct the price/qty on any non-void RCTI (including already-sent
  // and Xero-synced ones). Saving syncs the job, regenerates the PDF, resends the
  // email and void+recreates the Xero bill. A paid Xero bill is refused server-side,
  // so block editing here too (mirrors canVoid).
  const canEditLineItem = rcti &&
                          rcti.status !== 'void' &&
                          rcti.xero_invoice_status !== 'VOIDED' &&
                          rcti.xero_invoice_status !== 'PAID';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaFileInvoiceDollar className="mr-3" /> RCTI Details
        </h1>
        <div className="flex items-center">
            <button 
                onClick={fetchRCTIData} 
                className="text-jims-cyan-dark hover:text-jims-cyan mr-4 p-2 rounded-full hover:bg-gray-200 transition duration-150"
                title="Refresh RCTI Data"
                disabled={loading || isSending || isUpdating}
            >
                <FaSync className={` ${loading && !isSending ? 'animate-spin' : ''}`} />
            </button>
            <button 
                onClick={goBack}
                className="text-jims-cyan-dark hover:text-jims-cyan underline flex items-center"
            >
                <FaArrowLeft className="mr-2" /> {backLabel}
            </button>
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}

      {xeroExistsNotice && (
        <div className="mb-4 p-3 bg-cyan-50 text-cyan-800 border border-cyan-300 rounded flex items-start">
          <FaInfoCircle className="mr-2 mt-1 flex-shrink-0" />
          <span>{xeroExistsNotice}</span>
        </div>
      )}

      {sendToXeroError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {sendToXeroError}
        </div>
      )}

      {rcti && (
        <div className="mb-6 flex justify-end space-x-4">
          <button
            onClick={handleSendToXero}
            disabled={isSending || loading || isUpdating || isEditingLineItem || !canSendToXero}
            className={`bg-jims-green hover:bg-jims-green-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 ${isSending || loading || isUpdating || isEditingLineItem || !canSendToXero ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isVoided ? 'This RCTI has been voided.' : alreadySentToXero ? 'This RCTI has already been sent to Xero.' : 'Send this RCTI to Xero'}
          >
            {isSending ? (
                <><FaSync className="animate-spin mr-2" /> Sending...</>
            ) : (
                <><FaPaperPlane className="mr-2" /> Send to Xero</>
            )}
          </button>

          {canSyncFromXero && (
            <button
              onClick={handleSyncFromXero}
              disabled={isSyncing || isUpdating || isEditingLineItem}
              className={`bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 ${isSyncing || isUpdating || isEditingLineItem ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <FaSync className={`mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync from Xero'}
            </button>
          )}
        </div>
      )}

      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* Column 1 */}
          <div>
            <DetailItem label="RCTI ID" value={rcti.id} />
            <DetailItem label="Invoice Number" value={rcti.InvoiceNumber} />
            <DetailItem
              label="Status"
              value={displayStatus ? displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1) : 'N/A'}
              highlight={statusHighlight}
            />
            {canVoid && (
              <div className="mb-2">
                <button
                  onClick={() => setShowVoidConfirm(true)}
                  className="text-red-600 hover:text-red-800 text-sm flex items-center mt-1"
                >
                  <FaBan className="mr-1" size={12} /> Void RCTI
                </button>
              </div>
            )}
            <DetailItem label="Job ID" value={rcti.jobId} linkTo={`/jobs-list/edit/${rcti.jobId}`} />
            <DetailItem label="Reference" value={rcti.Reference} />
            <DetailItem label="RCTI Date" value={formatDate(rcti.RCTIDate)} />
            <DetailItem label="Due Date" value={formatDate(rcti.DueDate)} />
            <DetailItem label="Currency" value={rcti.Currency || 'AUD'} />
            <DetailItem label="Branding Theme" value={rcti.BrandingTheme || 'N/A'} />
          </div>

          {/* Column 2 */}
          <div>
            <h3 className="text-xl font-semibold text-jims-cyan-dark mb-3 border-b pb-2">Contact & Address</h3>
            <DetailItem label="Contact Name" value={rcti.SupplierName} />
            <DetailItem label="Email Address" value={rcti.SupplierEmail} />
            <DetailItem label="Phone" value={rcti.SupplierPhone} />
            <DetailItem label="Xero Contact ID" value={rcti.XeroContactId || 'N/A'} />
            <p className="text-sm font-medium text-gray-500 mt-3">Supplier Address:</p>
            <p className="text-gray-800">
                {rcti.SupplierAddressLine1 || ''}<br />
                {rcti.SupplierAddressLine2 ? <>{rcti.SupplierAddressLine2}<br /></> : ''}
                {rcti.SupplierCity || ''}{rcti.SupplierState ? `, ${rcti.SupplierState}` : ''}<br />
                {rcti.SupplierPostalCode || ''}{rcti.SupplierCountry ? `, ${rcti.SupplierCountry}` : ''}
            </p>
          </div>
        </div>

        {/* RCTI PDF: download when available, plus a regenerate action (rebuilds the
            file and refreshes the link; no Xero impact) for records whose PDF failed. */}
        <div className="mt-4">
          <DetailItem label="RCTI PDF" value={
            <span className="flex flex-wrap items-center gap-3">
              {rcti.pdfDownloadUrl ? (
                <button
                  onClick={handleViewPdf}
                  disabled={isLoadingPdf}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFilePdf className="mr-1.5" />
                  {isLoadingPdf ? 'Opening...' : 'Download PDF'}
                </button>
              ) : (
                <span className="text-sm text-gray-500">PDF not available.</span>
              )}
              <button
                onClick={() => setShowRegenerateConfirm(true)}
                disabled={isRegeneratingPdf || isUpdating || isVoiding || isSending}
                title="Rebuild the PDF and refresh its download link. Does not affect Xero."
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-jims-cyan-dark bg-white border border-jims-cyan-dark rounded hover:bg-jims-cyan hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSync className={`mr-1.5 ${isRegeneratingPdf ? 'animate-spin' : ''}`} />
                {isRegeneratingPdf ? 'Regenerating...' : 'Regenerate PDF'}
              </button>
            </span>
          } />
        </div>

        {/* Line Items Section */}
        <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between gap-4 mb-3">
                <h3 className="text-xl font-semibold text-jims-cyan-dark">Line Item</h3>
                {canEditLineItem && !isEditingLineItem && (
                    <button
                        type="button"
                        onClick={() => {
                          setSuccessMessage('');
                          setSendToXeroError(null);
                          setIsEditingLineItem(true);
                        }}
                        className="text-sm font-semibold text-jims-cyan-dark hover:text-jims-cyan"
                    >
                        Edit Line Item
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                <DetailItem label="Description" value={rcti.Description} fullWidth={true} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4 mt-4">
                {isEditingLineItem ? (
                  <>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Quantity</p>
                      <input
                        type="number"
                        name="Quantity"
                        min="0"
                        step="0.01"
                        value={lineItemForm.Quantity}
                        onChange={handleLineItemInputChange}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-gray-800 focus:border-jims-cyan focus:outline-none"
                      />
                    </div>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Unit Amount</p>
                      <input
                        type="number"
                        name="UnitAmount"
                        min="0"
                        step="0.01"
                        value={lineItemForm.UnitAmount}
                        onChange={handleLineItemInputChange}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-gray-800 focus:border-jims-cyan focus:outline-none"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <DetailItem label="Quantity" value={rcti.Quantity} />
                    <DetailItem label="Unit Amount" value={`$${rcti.UnitAmount ? rcti.UnitAmount.toFixed(2) : '0.00'}`} />
                  </>
                )}
                <DetailItem label="Discount" value={rcti.Discount !== null ? `${rcti.Discount}%` : 'N/A'} />
                <DetailItem label="Amount Due" value={`$${amountDue}`} highlight="blue" />
            </div>
            {isEditingLineItem && (
              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelLineItemEdit}
                  disabled={isUpdating}
                  className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRequestSave}
                  disabled={isUpdating || !hasLineItemChanges}
                  className="rounded bg-jims-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-jims-cyan-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
        </div>
        
        {/* Financial & Tracking Details */}
        <div className="mt-6 pt-6 border-t">
            <h3 className="text-xl font-semibold text-jims-cyan-dark mb-3">Financial & Tracking</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <DetailItem label="Account Code" value={rcti.AccountCode} />
                <DetailItem label="Tax Type" value={rcti.TaxType} />
                {rcti.TrackingName1 && <DetailItem label={rcti.TrackingName1} value={rcti.TrackingOption1} />}
                {rcti.TrackingName2 && <DetailItem label={rcti.TrackingName2} value={rcti.TrackingOption2} />}
            </div>
        </div>

        {/* Xero Submission Details */}
        {(rcti.xero_submission_status || rcti.xeroInvoiceId || canManuallyLink) && (
            <div className="mt-6 pt-6 border-t">
                <h3 className="text-xl font-semibold text-jims-cyan-dark mb-3">Xero Submission Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {rcti.xero_submission_status && 
                        <DetailItem 
                            label="Xero Submission Status" 
                            value={rcti.xero_submission_status.charAt(0).toUpperCase() + rcti.xero_submission_status.slice(1)}
                            highlight={rcti.xero_submission_status === 'failed' ? 'red' : rcti.xero_submission_status === 'success' ? 'green' : 'gray'}
                        />
                    }
                    <DetailItem label="Xero Invoice ID" value={rcti.xeroInvoiceId || 'N/A'} />
                </div>
                {rcti.xero_submission_status === 'failed' && rcti.xero_submission_failure_reason && (
                    <div className="mt-4">
                        <DetailItem label="Xero Submission Failure Reason" value={rcti.xero_submission_failure_reason} fullWidth={true} highlight="red-text" />
                    </div>
                )}
                {canManuallyLink && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">
                            Already created this bill by hand in Xero? Paste its Xero ID or go.xero.com URL to link it instead of creating a new one.
                        </p>
                        <p className="text-sm text-gray-500 mb-2">
                            To find the ID, open the bill in Xero and look at the web address at the top of your browser. The ID is the long code of letters and numbers with dashes, usually after <span className="font-mono">InvoiceID=</span>. You can copy the whole address and paste it here: Loope picks out the ID itself.
                        </p>
                        {/* A bill's address in Xero (AccountsPayable), not a sales invoice's, so it matches what staff see. */}
                        <p className="text-sm text-gray-500 mb-2" data-testid="xero-bill-id-example">
                            For example, in{' '}
                            <span className="break-all font-mono">
                                https://go.xero.com/AccountsPayable/View.aspx?InvoiceID=
                                <span className="rounded bg-yellow-200 px-1 font-semibold text-gray-800">8f3a2c1d-4b6e-4f7a-9c2d-5e8b1a3f6d90</span>
                            </span>{' '}
                            the highlighted part is the ID.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="text"
                                value={xeroBillInput}
                                onChange={(e) => setXeroBillInput(e.target.value)}
                                placeholder="Xero bill ID or go.xero.com URL"
                                className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm focus:border-jims-cyan focus:outline-none"
                                disabled={isLinking}
                            />
                            <button
                                type="button"
                                onClick={handleLinkXeroBill}
                                disabled={isLinking || !xeroBillInput.trim()}
                                className={`inline-flex items-center justify-center rounded bg-jims-cyan px-4 py-2 text-sm font-semibold text-white transition duration-300 hover:bg-jims-cyan-dark ${(isLinking || !xeroBillInput.trim()) ? 'cursor-not-allowed opacity-50' : ''}`}
                            >
                                <FaLink className="mr-2" />
                                {isLinking ? 'Linking...' : 'Link Xero Bill'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        )}

        {/* Timestamps */}
        <div className="mt-6 pt-6 border-t text-sm text-gray-500">
            <DetailItem label="Created At" value={formatDateTime(rcti.createdAt)} />
            <DetailItem label="Last Updated At" value={formatDateTime(rcti.updatedAt)} />
        </div>

      </div>

      <ActivityLog
        entityType="rcti"
        entityId={rctiId}
        createdAt={rcti.createdAt}
        createdLabel="RCTI created"
      />

      <ConfirmationModal
        isOpen={showVoidConfirm}
        onClose={() => setShowVoidConfirm(false)}
        onConfirm={handleVoidRCTI}
        title="Void RCTI"
        message={`Are you sure you want to void RCTI ${rcti?.InvoiceNumber || ''}? This action cannot be undone. If the RCTI has been sent to Xero, it will also be voided there.`}
        confirmText="Void RCTI"
        variant="danger"
        isLoading={isVoiding}
        loadingText="Voiding..."
      />

      <ConfirmationModal
        isOpen={showRegenerateConfirm}
        onClose={() => setShowRegenerateConfirm(false)}
        onConfirm={handleRegeneratePdf}
        title="Regenerate PDF"
        message={`Rebuild the PDF for RCTI ${rcti?.RCTINumber || rcti?.InvoiceNumber || ''} and refresh its download link? This replaces the stored PDF file and does not affect Xero.`}
        confirmText="Regenerate PDF"
        variant="primary"
        isLoading={isRegeneratingPdf}
        loadingText="Regenerating..."
      />

      <ConfirmationModal
        isOpen={showEditConfirm}
        onClose={() => setShowEditConfirm(false)}
        onConfirm={handleConfirmedSave}
        title="Update RCTI pricing"
        message={
          <span className="block text-left space-y-3">
            <span className="block text-gray-700">
              You're about to update{' '}
              <strong>RCTI {rcti?.RCTINumber || rcti?.InvoiceNumber || 'this RCTI'}</strong>.
            </span>

            <span className="block rounded bg-gray-50 p-3 text-gray-700">
              <span className="block">
                Amount: <strong>{formatCurrency(beforeTotal)}</strong>
                <span className="mx-1">→</span>
                <strong className="text-gray-900">{formatCurrency(afterTotal)}</strong>
              </span>
              <span className="block text-xs text-gray-500">
                {editedQuantity ?? '?'} × {formatCurrency(editedUnitAmount)} each
                {' '}(was {currentQuantity ?? '?'} × {formatCurrency(currentUnitAmount)})
              </span>
            </span>

            <span className="block">
              <span className="block font-medium text-gray-700">This will also:</span>
              <span className="block">
                •{' '}
                {!hasLinkedJob ? (
                  <>This RCTI isn't linked to a job, so no franchisee job price changes.</>
                ) : unitAmountChanged ? (
                  <>
                    Update the franchisee's price on the linked job from{' '}
                    {formatCurrency(currentUnitAmount)} to{' '}
                    <strong>{formatCurrency(editedUnitAmount)}</strong>.
                  </>
                ) : (
                  <>Leave the franchisee's price on the linked job unchanged.</>
                )}
              </span>
              <span className="block">• Create a new PDF for this RCTI.</span>
              <span className="block">
                • Email the updated RCTI to{' '}
                <strong>{rcti?.SupplierEmail || 'the franchisee'}</strong>.
              </span>
              {rcti?.xeroInvoiceId ? (
                <span className="block text-amber-700">
                  • Replace this RCTI's bill in Xero with the corrected one. If it's already
                  been paid in Xero, the update is cancelled and nothing changes.
                </span>
              ) : null}
            </span>
          </span>
        }
        confirmText="Update & Resend"
        variant="warning"
        isLoading={isUpdating}
        loadingText="Updating..."
      />
    </div>
  );
};

const DetailItem = ({ label, value, linkTo, highlight, fullWidth }) => {
    const valueClasses = `text-gray-800 ${fullWidth ? 'col-span-full' : ''}`;
    let content = value;
    
    if (linkTo && value !== 'N/A') {
        content = <Link to={linkTo} className="text-jims-cyan hover:underline">{value}</Link>;
    }
    
    let highlightClass = '';
    if (highlight) {
        switch (highlight) {
            case 'yellow': highlightClass = 'bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'green': highlightClass = 'bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'blue': highlightClass = 'text-blue-600 font-bold'; break;
            case 'red': highlightClass = 'bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'red-text': highlightClass = 'text-red-700 font-medium'; break;
            default: highlightClass = 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
        }
        content = <span className={highlightClass}>{value}</span>;
    }

    return (
        <div className={`mb-2 ${fullWidth ? 'col-span-full' : ''}`}>
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <p className={valueClasses}>{content}</p>
        </div>
    );
};

export default RCTIDetailsPage;
